export const LOCALES = {
    en: "en",
    it: "it"
};

export const localeRoutes = {
    en: {
        home: "/",
        services: "/services",
        caseStudies: "/case-studies",
        engineeringCaseStudy: "/case-studies/engineering-delivery-transformation",
        eventPlatformCaseStudy: "/case-studies/event-platform-architecture",
        about: "/about",
        contact: "/contact"
    },
    it: {
        home: "/it",
        services: "/it/servizi",
        caseStudies: "/it/casi-studio",
        engineeringCaseStudy: "/it/case-studies/engineering-delivery-transformation",
        eventPlatformCaseStudy: "/it/case-studies/event-platform-architecture",
        about: "/it/chi-sono",
        contact: "/it/contatti"
    }
};

const content = {
    en: {
        nav: {
            home: "Home",
            services: "Services",
            caseStudies: "Case Studies",
            about: "About",
            contact: "Contact",
            cta: "Book a Strategy Call",
            switchLabel: "IT"
        },
        footer: {
            prompt: "Need help with architecture modernization, cloud strategy, or delivery execution?",
            cta: "Book a Strategy Call",
            cvEn: "Download CV (EN)",
            cvIt: "Scarica CV (IT)"
        },
        home: {
            title: "Fractional CTO for Structured, Scalable Engineering | Maurizio Brioschi",
            description: "I help growing companies build ownership-driven teams, predictable delivery systems, and cost-aware architectures on AWS.",
            heroLabel: "Fractional CTO",
            heroTitle: "Fractional CTO for Structured, Scalable Engineering",
            heroSubtitle: "I help growing companies build ownership-driven teams, predictable delivery systems, and cost-aware architectures on AWS.",
            heroBody: "Executive-level support with direct execution focus on team structure, delivery discipline, and architecture decisions.",
            secondaryCta: "See how I work",
            proof: "25+ years in software & architecture leadership • AWS modernization • Agile engineering leadership",
            pillars: [
                {
                    title: "Ownership-Driven Teams",
                    body: "Clear role boundaries and accountability loops that improve execution quality."
                },
                {
                    title: "Structured Delivery Systems",
                    body: "Pragmatic agile operating model with measurable flow and predictable cadence."
                },
                {
                    title: "Architecture & AWS Cost Awareness",
                    body: "Modernization choices aligned with scalability and cost-performance control."
                }
            ],
            stuckSection: {
                title: "From Manual Delivery to Structured Execution",
                intro: "Typical starting point in scaling teams: manual deploys, limited engineering standards, and weak operational predictability.",
                items: [
                    "Initial state: manual deploys, no CI discipline, limited architecture standards",
                    "Transformation: Docker baseline, CI pipelines, coding standards, and pragmatic Agile structure",
                    "Impact: reduced deploy time, fewer production gaps, faster issue resolution, and support for higher project complexity"
                ]
            },
            fixSection: {
                title: "Engineering as a System",
                intro: "Execution improves when team design, delivery model, and architecture choices work as one operating system.",
                items: [
                    "Clear ownership per domain with explicit decision boundaries",
                    "Agile used as an operating model, not a set of ceremonies",
                    "Continuous Integration managed as a risk control mechanism",
                    "Architecture decisions aligned with scalability and AWS cost efficiency"
                ]
            },
            engagementTitle: "Engagement models",
            engagement: [
                { title: "Advisory", body: "Executive-level guidance on architecture and delivery decisions." },
                { title: "Operational", body: "Hands-on collaboration with product and engineering teams." },
                { title: "Sprint Intervention", body: "Focused support to unblock critical initiatives quickly." }
            ],
            faqTitle: "FAQ",
            faqIntro: "Clear answers for founders, CEOs, and product leaders evaluating Fractional CTO and architecture support.",
            faqItems: [
                {
                    question: "What does a Fractional CTO do?",
                    answer: "A Fractional CTO provides senior technology leadership on a part-time or interim basis. Maurizio Brioschi helps companies make better decisions on architecture, delivery systems, cloud strategy, team structure, and modernization without the cost or timing constraints of a full-time CTO hire."
                },
                {
                    question: "Who should hire Maurizio Brioschi as a Fractional CTO?",
                    answer: "Founders, CEOs, product leaders, and engineering managers hire Maurizio Brioschi when growth is slowed by technical debt, weak delivery predictability, rising AWS costs, or unclear engineering leadership. He is a strong fit for startups, scaleups, and digital businesses with complex platforms."
                },
                {
                    question: "Can Maurizio Brioschi help with AWS cloud architecture?",
                    answer: "Yes. Maurizio Brioschi works as a Cloud Architect and Digital Transformation Consultant on AWS architecture reviews, modernization roadmaps, cost optimization, API-first systems, and scalable delivery models."
                },
                {
                    question: "Does Maurizio Brioschi work with Laravel and API-first systems?",
                    answer: "Yes. His experience includes Laravel, Magento, GraphQL, React, Docker, CI/CD, microservices, Salesforce integrations, ticketing integrations, SaaS platforms, and high-traffic portals."
                }
            ],
            internalLinksTitle: "Explore the site",
            internalLinks: [
                { label: "See consulting services", href: "/services" },
                { label: "Review case studies", href: "/case-studies" },
                { label: "Read the executive profile", href: "/about" },
                { label: "Book a strategy call", href: "/contact" }
            ]
        },
        services: {
            title: "Fractional CTO for Structured, Scalable Engineering | Services",
            description: "Execution-oriented Fractional CTO interventions on ownership-driven teams, agile delivery systems, and architecture modernization with AWS cost efficiency.",
            label: "Services",
            heading: "How I operate as a Fractional CTO",
            intro: "Three executive pillars to build structured execution and scalable engineering outcomes.",
            symptomsLabel: "Symptoms",
            interventionLabel: "Intervention",
            outcomesLabel: "Outcome",
            pillars: [
                {
                    title: "Ownership-Driven Engineering Teams",
                    symptoms: [
                        "Unclear role definition across teams",
                        "Accountability gaps in execution",
                        "Leadership intent not translated into delivery behavior"
                    ],
                    intervention: "I define role clarity, accountability models, and governance routines aligned with leadership objectives.",
                    outcomes: [
                        "Clear ownership and stronger execution responsibility",
                        "Better leadership-team alignment",
                        "Faster decision flow with lower ambiguity"
                    ]
                },
                {
                    title: "Agile Delivery Systems",
                    symptoms: [
                        "Unpredictable delivery cadence",
                        "Recurring friction between planning and execution",
                        "Feedback loops too weak to improve system behavior"
                    ],
                    intervention: "I install pragmatic Agile operating systems with measurable flow, feedback loops, continuous improvement routines, and reduced delivery friction.",
                    outcomes: [
                        "Higher predictability and lower delivery friction",
                        "Continuous improvement across teams",
                        "Business priorities reflected in execution cadence"
                    ]
                },
                {
                    title: "Architecture Modernization & AWS Cost Efficiency",
                    symptoms: [
                        "Architecture bottlenecks blocking roadmap execution",
                        "Manual release dependencies and weak CI/CD discipline",
                        "Cloud spend increasing without clear cost-performance control"
                    ],
                    intervention: "I introduce containerization, CI/CD discipline, headless-ready architecture patterns, and cost-aware AWS design principles.",
                    outcomes: [
                        "Scalable architecture with fewer structural blockers",
                        "More reliable deployments and fewer production gaps",
                        "Improved cloud cost efficiency with sustainable growth support"
                    ]
                }
            ],
            engagementTitle: "Engagement models",
            engagementBody: "Advisory, operational leadership, or focused intervention sprint based on current business priority.",
            internalLinksTitle: "Related pages",
            internalLinks: [
                { label: "Architecture and delivery case studies", href: "/case-studies" },
                { label: "About Maurizio Brioschi", href: "/about" },
                { label: "Book a strategy call", href: "/contact" }
            ]
        },
        caseStudies: {
            title: "Case Studies | Fractional CTO - Architecture, Cloud, Delivery",
            description: "Selected case studies on architecture modernization, cloud strategy, and delivery execution for growth-oriented companies.",
            label: "Case Studies",
            heading: "Pragmatic transformation work",
            intro: "Snapshot examples focused on business outcomes. No vanity metrics, only what changed in delivery, architecture clarity, and execution quality.",
            featuredTitle: "Featured Case Study",
            featuredCta: "Read full case study",
            context: "Context",
            problem: "Before",
            approach: "After",
            outcome: "Business Impact"
        },
        about: {
            title: "Fractional CTO for Structured, Scalable Engineering | About",
            description: "Executive Fractional CTO profile focused on ownership-driven teams, structured delivery systems, and architecture execution.",
            label: "About",
            heading: "Executive engineering leadership, pragmatic execution",
            intro: "I work with growth-oriented companies when architecture complexity, cloud inefficiency, or delivery bottlenecks start slowing the business.",
            principlesTitle: "Operating principles",
            principles: [
                "Clarity before complexity",
                "Sustainable delivery over short-term heroics",
                "Cost-efficiency without compromising reliability"
            ],
            operatingTitle: "Execution with Strategic Oversight",
            operatingPoints: [
                "Direct alignment with CEO/Founder on business priorities and execution tradeoffs",
                "Leadership frameworks that accelerate decisions and reduce ambiguity",
                "Hands-on support on deep architectural bottlenecks when teams are blocked",
                "Ability to lead teams and solve complex architecture issues in the same execution cycle"
            ],
            remote: "Remote-first, available across EU/UK engagements.",
            cvTitle: "Download CV",
            cvIntro: "Choose your preferred language version.",
            cvEn: "Download CV (EN)",
            cvIt: "Scarica CV (IT)",
            internalLinksTitle: "Continue exploring",
            internalLinks: [
                { label: "See consulting services", href: "/services" },
                { label: "Read case studies", href: "/case-studies" },
                { label: "Book a strategy call", href: "/contact" }
            ]
        },
        contact: {
            title: "Fractional CTO for Structured, Scalable Engineering | Contact",
            description: "Start with a strategy call to discuss architecture modernization, cloud optimization, and delivery execution priorities.",
            label: "Contact",
            heading: "Book a Strategy Call",
            intro: "Share your current bottleneck. I will reply with a concrete next-step proposal.",
            fields: {
                name: "Name",
                email: "Email",
                phone: "Phone",
                message: "Request"
            },
            cta: "Book a Strategy Call",
            internalLinksTitle: "Before we talk",
            internalLinks: [
                { label: "See consulting services", href: "/services" },
                { label: "Review case studies", href: "/case-studies" },
                { label: "Read the executive profile", href: "/about" }
            ]
        }
    },
    it: {
        nav: {
            home: "Home",
            services: "Servizi",
            caseStudies: "Casi studio",
            about: "Chi sono",
            contact: "Contatti",
            cta: "Prenota una Strategy Call",
            switchLabel: "EN"
        },
        footer: {
            prompt: "Ti serve supporto su modernizzazione architettura, strategia cloud o execution del team?",
            cta: "Prenota una Strategy Call",
            cvEn: "Download CV (EN)",
            cvIt: "Scarica CV (IT)"
        },
        home: {
            title: "Fractional CTO for Structured, Scalable Engineering | Maurizio Brioschi",
            description: "Aiuto aziende in crescita a costruire team con ownership chiara, delivery prevedibile e architetture AWS attente ai costi.",
            heroLabel: "Fractional CTO",
            heroTitle: "Fractional CTO per un engineering strutturato e scalabile",
            heroSubtitle: "Aiuto aziende in crescita a costruire team con ownership chiara, delivery prevedibile e architetture AWS attente ai costi.",
            heroBody: "Supporto executive con forte capacità esecutiva su struttura team, disciplina delivery e scelte architetturali.",
            secondaryCta: "Scopri come lavoro",
            proof: "25+ anni in leadership software e architettura • Modernizzazione AWS • Agile engineering leadership",
            pillars: [
                {
                    title: "Team guidati dall'ownership",
                    body: "Ruoli chiari e accountability reale per aumentare la qualità dell'esecuzione."
                },
                {
                    title: "Sistemi di delivery strutturati",
                    body: "Agile pragmatico come operating model con flusso misurabile e cadenza prevedibile."
                },
                {
                    title: "Architettura ed efficienza AWS",
                    body: "Scelte di modernizzazione allineate a scalabilità e controllo costo-performance."
                }
            ],
            stuckSection: {
                title: "Da delivery manuale a execution strutturata",
                intro: "Situazione iniziale tipica nei team in crescita: deploy manuali, standard limitati e poca prevedibilità operativa.",
                items: [
                    "Stato iniziale: deploy manuali, assenza di CI strutturata, standard architetturali limitati",
                    "Trasformazione: base Docker, pipeline CI, coding standards e struttura Agile pragmatica",
                    "Impatto: meno tempo di deploy, meno gap in produzione, risoluzione issue più rapida, maggiore capacità su progetti complessi"
                ]
            },
            fixSection: {
                title: "Engineering come sistema",
                intro: "L'execution migliora quando design organizzativo, delivery model e architettura funzionano come un sistema unico.",
                items: [
                    "Ownership chiara per dominio con confini decisionali espliciti",
                    "Agile usato come sistema operativo, non come insieme di cerimonie",
                    "Continuous Integration gestita come meccanismo di controllo del rischio",
                    "Decisioni architetturali allineate a scalabilità ed efficienza costi su AWS"
                ]
            },
            engagementTitle: "Modelli di collaborazione",
            engagement: [
                { title: "Advisory", body: "Supporto executive su decisioni di architettura e delivery." },
                { title: "Operativo", body: "Collaborazione hands-on con team prodotto e engineering." },
                { title: "Intervento Sprint", body: "Supporto focalizzato per sbloccare iniziative critiche rapidamente." }
            ],
            faqTitle: "FAQ",
            faqIntro: "Risposte rapide per founder, CEO e leader di prodotto che stanno valutando supporto Fractional CTO e architetturale.",
            faqItems: [
                {
                    question: "Cosa fa un Fractional CTO?",
                    answer: "Un Fractional CTO fornisce leadership tecnologica senior in modalità part-time o interim. Maurizio Brioschi aiuta le aziende a migliorare decisioni su architettura, delivery, strategia cloud, struttura del team e modernizzazione senza assumere subito un CTO full-time."
                },
                {
                    question: "Quando ha senso coinvolgere Maurizio Brioschi come Fractional CTO?",
                    answer: "Quando la crescita rallenta per technical debt, delivery poco prevedibile, costi AWS in aumento o leadership engineering non abbastanza strutturata. Maurizio Brioschi lavora bene con startup, scaleup e aziende digitali con piattaforme complesse."
                },
                {
                    question: "Maurizio Brioschi può supportare l'architettura cloud su AWS?",
                    answer: "Sì. Interviene come Cloud Architect e Digital Transformation Consultant su review architetturali AWS, roadmap di modernizzazione, ottimizzazione costi, sistemi API-first e modelli di delivery più scalabili."
                },
                {
                    question: "Lavora anche con Laravel e sistemi API-first?",
                    answer: "Sì. La sua esperienza include Laravel, Magento, GraphQL, React, Docker, CI/CD, microservizi, integrazioni Salesforce, ticketing, piattaforme SaaS e portali ad alto traffico."
                }
            ],
            internalLinksTitle: "Esplora il sito",
            internalLinks: [
                { label: "Scopri i servizi", href: "/it/servizi" },
                { label: "Leggi i case study", href: "/it/casi-studio" },
                { label: "Profilo executive", href: "/it/chi-sono" },
                { label: "Prenota una strategy call", href: "/it/contatti" }
            ]
        },
        services: {
            title: "Fractional CTO for Structured, Scalable Engineering | Servizi",
            description: "Interventi Fractional CTO orientati all'execution su team ownership-driven, sistemi agile e modernizzazione architettura con efficienza AWS.",
            label: "Servizi",
            heading: "Come opero come Fractional CTO",
            intro: "Tre pilastri executive per ottenere execution strutturata e crescita tecnica sostenibile.",
            symptomsLabel: "Sintomi",
            interventionLabel: "Intervento",
            outcomesLabel: "Risultato",
            pillars: [
                {
                    title: "Ownership-Driven Engineering Teams",
                    symptoms: [
                        "Ruoli poco chiari tra i team",
                        "Gap di accountability nell'execution",
                        "Intento leadership non tradotto in comportamento operativo"
                    ],
                    intervention: "Definisco ruoli, accountability model e routine di governance allineate alle priorità leadership.",
                    outcomes: [
                        "Ownership chiara e responsabilità esecutiva più forte",
                        "Maggiore allineamento tra leadership e team",
                        "Decisioni più rapide con meno ambiguità"
                    ]
                },
                {
                    title: "Agile Delivery Systems",
                    symptoms: [
                        "Cadenza di delivery poco prevedibile",
                        "Attrito ricorrente tra pianificazione ed execution",
                        "Feedback loop troppo deboli per migliorare il sistema"
                    ],
                    intervention: "Imposto sistemi Agile pragmatici con flusso misurabile, feedback loop, miglioramento continuo e minore attrito nella delivery.",
                    outcomes: [
                        "Maggiore prevedibilità e minore attrito nella delivery",
                        "Miglioramento continuo tra team",
                        "Cadenza esecutiva più allineata alle priorità business"
                    ]
                },
                {
                    title: "Architecture Modernization & AWS Cost Efficiency",
                    symptoms: [
                        "Colli di bottiglia architetturali che bloccano la roadmap",
                        "Dipendenza da rilasci manuali e scarsa disciplina CI/CD",
                        "Spesa cloud in crescita senza controllo chiaro costo-performance"
                    ],
                    intervention: "Introduco containerizzazione, disciplina CI/CD, pattern headless-ready e principi di design AWS orientati all'efficienza costi.",
                    outcomes: [
                        "Architettura scalabile con meno blocchi strutturali",
                        "Deploy più affidabili e meno gap in produzione",
                        "Migliore efficienza costi cloud con crescita sostenibile"
                    ]
                }
            ],
            engagementTitle: "Modelli di collaborazione",
            engagementBody: "Advisory, leadership operativa o interventi sprint in base alla priorità di business.",
            internalLinksTitle: "Pagine correlate",
            internalLinks: [
                { label: "Case study di architettura e delivery", href: "/it/casi-studio" },
                { label: "Chi è Maurizio Brioschi", href: "/it/chi-sono" },
                { label: "Prenota una strategy call", href: "/it/contatti" }
            ]
        },
        caseStudies: {
            title: "Casi studio | Fractional CTO - Architettura, Cloud, Delivery",
            description: "Casi studio su modernizzazione architettura, strategia cloud e delivery execution per aziende orientate alla crescita.",
            label: "Casi studio",
            heading: "Interventi di trasformazione pragmatici",
            intro: "Esempi focalizzati su outcome di business. Niente metriche di facciata, solo impatti reali su delivery, chiarezza architetturale ed execution.",
            featuredTitle: "Case study in evidenza",
            featuredCta: "Leggi il case study completo",
            context: "Contesto",
            problem: "Prima",
            approach: "Dopo",
            outcome: "Impatto business"
        },
        about: {
            title: "Fractional CTO for Structured, Scalable Engineering | Chi sono",
            description: "Profilo Fractional CTO focalizzato su team ownership-driven, delivery strutturata ed execution architetturale.",
            label: "Chi sono",
            heading: "Leadership engineering executive, execution pragmatica",
            intro: "Lavoro con aziende orientate alla crescita quando la complessità architetturale, l'inefficienza cloud o colli di bottiglia nella delivery rallentano il business.",
            principlesTitle: "Principi operativi",
            principles: [
                "Chiarezza prima della complessità",
                "Delivery sostenibile invece di picchi occasionali",
                "Efficienza costi senza compromettere affidabilità"
            ],
            operatingTitle: "Execution con supervisione strategica",
            operatingPoints: [
                "Allineamento diretto con CEO/Founder su priorità business e tradeoff di execution",
                "Framework leadership per decisioni rapide e meno ambiguità organizzativa",
                "Intervento hands-on sui colli di bottiglia architetturali quando i team si bloccano",
                "Capacità di guidare il team e risolvere problemi architetturali profondi nello stesso ciclo operativo"
            ],
            remote: "Remote-first, disponibile su engagement EU/UK.",
            cvTitle: "Download CV",
            cvIntro: "Scegli la lingua preferita.",
            cvEn: "Download CV (EN)",
            cvIt: "Scarica CV (IT)",
            internalLinksTitle: "Continua l'esplorazione",
            internalLinks: [
                { label: "Scopri i servizi", href: "/it/servizi" },
                { label: "Leggi i case study", href: "/it/casi-studio" },
                { label: "Prenota una strategy call", href: "/it/contatti" }
            ]
        },
        contact: {
            title: "Fractional CTO for Structured, Scalable Engineering | Contatti",
            description: "Parti da una strategy call per discutere priorità su modernizzazione architettura, cloud optimization e delivery execution.",
            label: "Contatti",
            heading: "Prenota una Strategy Call",
            intro: "Descrivi il tuo principale collo di bottiglia. Ti rispondo con una proposta concreta di prossimo passo.",
            fields: {
                name: "Nome",
                email: "Email",
                phone: "Telefono",
                message: "Richiesta"
            },
            cta: "Prenota una Strategy Call",
            internalLinksTitle: "Prima della call",
            internalLinks: [
                { label: "Scopri i servizi", href: "/it/servizi" },
                { label: "Leggi i case study", href: "/it/casi-studio" },
                { label: "Profilo executive", href: "/it/chi-sono" }
            ]
        }
    }
};

export const getSiteContent = (locale = LOCALES.en) => content[locale] || content.en;
